import { useState, useEffect } from 'react';
import { Sparkles, Loader2, Trash2, Eye, EyeOff, Plus, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const cur = (v) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

export default function CasesTab({ token }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState('');

    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    const load = async () => {
        setLoading(true);
        try {
            const r = await fetch(`${API}/api/testimonials`, { headers });
            setItems((await r.json()).testimonials || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const generate = async (form) => {
        setGenerating(true);
        setError('');
        try {
            const r = await fetch(`${API}/api/testimonials/generate`, {
                method: 'POST', headers,
                body: JSON.stringify({ ...form, taal: 'nl' })
            });
            if (!r.ok) throw new Error(await r.text());
            setShowForm(false);
            load();
        } catch (e) {
            setError('AI-generatie mislukt: ' + e.message);
        } finally {
            setGenerating(false);
        }
    };

    const del = async (id) => {
        if (!confirm('Verwijderen?')) return;
        await fetch(`${API}/api/testimonials/${id}`, { method: 'DELETE', headers });
        load();
    };

    const toggle = async (id) => {
        await fetch(`${API}/api/testimonials/${id}/toggle`, { method: 'PATCH', headers });
        load();
    };

    if (loading) return <div className="text-gray-500">Laden...</div>;

    return (
        <div className="space-y-4" data-testid="cases-tab">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold font-['Outfit']">Case Studies ({items.length})</h2>
                    <p className="text-sm text-gray-500">AI-gegenereerde klantcases op basis van omzetgegevens</p>
                </div>
                <button
                    onClick={() => setShowForm(true)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 rounded-lg text-sm font-medium"
                    data-testid="add-case-btn"
                >
                    <Plus className="w-4 h-4" /> Case genereren
                </button>
            </div>

            {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-2 text-sm">{error}</div>}

            {items.length === 0 ? (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center text-gray-500">
                    Nog geen case studies. Genereer er een op basis van omzet.
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2">
                    {items.map((t) => (
                        <div key={t.id} className={`bg-gray-900 border rounded-xl p-5 ${t.visible ? 'border-emerald-500/30' : 'border-gray-800 opacity-60'}`} data-testid={`case-card-${t.id}`}>
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <h3 className="font-bold text-white">{t.klant_naam}</h3>
                                    <p className="text-xs text-gray-500">{t.locatie_type} - {t.aantal_terminals} terminals</p>
                                </div>
                                <div className="text-right">
                                    <div className="text-emerald-400 font-bold text-sm">{t.highlight_stat}</div>
                                    <div className="text-xs text-gray-500">{cur(t.maandelijkse_omzet)}/mnd</div>
                                </div>
                            </div>
                            <blockquote className="text-gray-300 italic text-sm border-l-2 border-emerald-500/50 pl-3 mb-3">
                                "{t.quote}"
                            </blockquote>
                            <p className="text-gray-400 text-sm leading-relaxed">{t.case_study}</p>
                            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-800">
                                <button onClick={() => toggle(t.id)} className="text-xs text-gray-500 hover:text-white flex items-center gap-1" data-testid={`toggle-case-${t.id}`}>
                                    {t.visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                    {t.visible ? 'Zichtbaar' : 'Verborgen'}
                                </button>
                                <button onClick={() => del(t.id)} className="text-xs text-gray-500 hover:text-red-400 flex items-center gap-1" data-testid={`delete-case-${t.id}`}>
                                    <Trash2 className="w-3 h-3" /> Verwijder
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showForm && <GenerateCaseModal onClose={() => setShowForm(false)} onGenerate={generate} generating={generating} />}
        </div>
    );
}

function GenerateCaseModal({ onClose, onGenerate, generating }) {
    const [form, setForm] = useState({
        klant_naam: '',
        locatie_type: 'Camping',
        aantal_terminals: 8,
        maandelijkse_omzet: 1200,
    });

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" data-testid="generate-case-modal">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-lg">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-purple-400" /> AI Case Study genereren
                    </h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <div className="space-y-3">
                    <input
                        placeholder="Klantnaam (bijv. Camping De Duinen)"
                        value={form.klant_naam}
                        onChange={(e) => setForm({ ...form, klant_naam: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                        data-testid="case-input-klant"
                    />
                    <select
                        value={form.locatie_type}
                        onChange={(e) => setForm({ ...form, locatie_type: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                        data-testid="case-input-type"
                    >
                        {['Camping', 'Recreatiepark', 'Jachthaven', 'Camperplaats', 'Zwembad', 'Sportaccommodatie'].map(t => <option key={t}>{t}</option>)}
                    </select>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Aantal terminals</label>
                            <input
                                type="number"
                                value={form.aantal_terminals}
                                onChange={(e) => setForm({ ...form, aantal_terminals: parseInt(e.target.value) || 1 })}
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                                data-testid="case-input-terminals"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Maandomzet (EUR)</label>
                            <input
                                type="number"
                                value={form.maandelijkse_omzet}
                                onChange={(e) => setForm({ ...form, maandelijkse_omzet: parseFloat(e.target.value) || 0 })}
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                                data-testid="case-input-omzet"
                            />
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} className="px-4 py-2 text-gray-400 hover:text-white text-sm">Annuleren</button>
                    <button
                        onClick={() => onGenerate(form)}
                        disabled={generating || !form.klant_naam}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-sm font-medium disabled:opacity-50"
                        data-testid="generate-case-btn"
                    >
                        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        Genereer met AI
                    </button>
                </div>
            </div>
        </div>
    );
}
